import React from 'react';
import { motion } from 'framer-motion';

export const PrivacyPolicy: React.FC = () => {
  return (
    <div className="bg-ivory min-h-screen">
      {/* Header */}
      <header className="bg-sand/30 border-b border-taupe/20 py-8">
        <div className="container mx-auto px-4 max-w-4xl">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <a href="/" className="inline-block mb-4 text-bronze hover:text-charcoal transition-colors">
              ← Back to Home
            </a>
            <h1 className="font-cormorant text-4xl md:text-5xl font-semibold mb-2">Privacy Policy</h1>
            <p className="text-taupe">Last updated: {new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}</p>
          </motion.div>
        </div>
      </header>

      {/* Content */}
      <main className="container mx-auto px-4 py-12 max-w-4xl">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="prose prose-lg max-w-none"
        >
          <section className="mb-12">
            <h2 className="font-cormorant text-3xl font-semibold mb-4">Introduction</h2>
            <p className="text-taupe leading-relaxed mb-4">
              Holis Massage ("we", "our", or "us") is committed to protecting your privacy. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you visit our website holismassage.com and use our services.
            </p>
            <p className="text-taupe leading-relaxed">
              By using our website, you consent to the data practices described in this policy. If you do not agree with this policy, please do not access or use our website.
            </p>
          </section>

          <section className="mb-12">
            <h2 className="font-cormorant text-3xl font-semibold mb-4">Information We Collect</h2>
            
            <h3 className="font-cormorant text-2xl font-medium mb-3 mt-6">Personal Information</h3>
            <p className="text-taupe leading-relaxed mb-4">
              We may collect personal information that you voluntarily provide to us when you:
            </p>
            <ul className="list-disc pl-6 text-taupe space-y-2 mb-4">
              <li>Contact us via email, phone, or WhatsApp</li>
              <li>Fill out contact forms or questionnaires</li>
              <li>Apply for complimentary sessions</li>
              <li>Book massage or bodywork services</li>
            </ul>
            <p className="text-taupe leading-relaxed mb-4">
              This information may include:
            </p>
            <ul className="list-disc pl-6 text-taupe space-y-2">
              <li>Name</li>
              <li>Email address</li>
              <li>Phone number</li>
              <li>Location/address</li>
              <li>Health and wellness information (only as necessary for providing services)</li>
            </ul>

            <h3 className="font-cormorant text-2xl font-medium mb-3 mt-6">Automatically Collected Information</h3>
            <p className="text-taupe leading-relaxed mb-4">
              When you visit our website, we may automatically collect certain information about your device, including:
            </p>
            <ul className="list-disc pl-6 text-taupe space-y-2">
              <li>IP address</li>
              <li>Browser type and version</li>
              <li>Operating system</li>
              <li>Pages visited and time spent on pages</li>
              <li>Referring website addresses</li>
            </ul>
          </section>

          <section className="mb-12">
            <h2 className="font-cormorant text-3xl font-semibold mb-4">How We Use Your Information</h2>
            <p className="text-taupe leading-relaxed mb-4">
              We use the information we collect to:
            </p>
            <ul className="list-disc pl-6 text-taupe space-y-2">
              <li>Respond to your inquiries and provide customer service</li>
              <li>Schedule and manage appointments</li>
              <li>Provide personalized bodywork and massage services</li>
              <li>Send you information about our services (with your consent)</li>
              <li>Improve our website and services</li>
              <li>Comply with legal obligations</li>
              <li>Protect against fraudulent or illegal activity</li>
            </ul>
          </section>

          <section className="mb-12">
            <h2 className="font-cormorant text-3xl font-semibold mb-4">Cookies and Tracking Technologies</h2>
            <p className="text-taupe leading-relaxed mb-4">
              We use cookies and similar tracking technologies to enhance your experience on our website. Cookies are small data files stored on your device.
            </p>
            
            <h3 className="font-cormorant text-2xl font-medium mb-3 mt-6">Types of Cookies We Use:</h3>
            <ul className="list-disc pl-6 text-taupe space-y-2 mb-4">
              <li><strong>Essential Cookies:</strong> Necessary for the website to function properly</li>
              <li><strong>Preference Cookies:</strong> Remember your settings and preferences</li>
              <li><strong>Analytics Cookies:</strong> Help us understand how visitors use our website</li>
            </ul>
            
            <p className="text-taupe leading-relaxed">
              You can control cookies through your browser settings. However, disabling cookies may affect the functionality of our website.
            </p>
          </section>

          <section className="mb-12">
            <h2 className="font-cormorant text-3xl font-semibold mb-4">Data Sharing and Disclosure</h2>
            <p className="text-taupe leading-relaxed mb-4">
              We do not sell, trade, or rent your personal information to third parties. We may share your information only in the following circumstances:
            </p>
            <ul className="list-disc pl-6 text-taupe space-y-2">
              <li><strong>Service Providers:</strong> With trusted third-party service providers who assist us in operating our website and providing services (e.g., hosting, email services)</li>
              <li><strong>Legal Requirements:</strong> When required by law or to protect our rights, safety, or property</li>
              <li><strong>Business Transfers:</strong> In connection with a merger, acquisition, or sale of assets</li>
            </ul>
          </section>

          <section className="mb-12">
            <h2 className="font-cormorant text-3xl font-semibold mb-4">Your Rights Under GDPR</h2>
            <p className="text-taupe leading-relaxed mb-4">
              If you are a resident of the European Economic Area (EEA) or Switzerland, you have certain data protection rights under the General Data Protection Regulation (GDPR):
            </p>
            <ul className="list-disc pl-6 text-taupe space-y-2">
              <li><strong>Right to Access:</strong> Request access to your personal data</li>
              <li><strong>Right to Rectification:</strong> Request correction of inaccurate data</li>
              <li><strong>Right to Erasure:</strong> Request deletion of your personal data</li>
              <li><strong>Right to Restrict Processing:</strong> Request limitation of processing your data</li>
              <li><strong>Right to Data Portability:</strong> Request transfer of your data to another service</li>
              <li><strong>Right to Object:</strong> Object to processing of your personal data</li>
              <li><strong>Right to Withdraw Consent:</strong> Withdraw consent at any time</li>
            </ul>
            <p className="text-taupe leading-relaxed mt-4">
              To exercise these rights, please contact us at the information provided below.
            </p>
          </section>

          <section className="mb-12">
            <h2 className="font-cormorant text-3xl font-semibold mb-4">Data Security</h2>
            <p className="text-taupe leading-relaxed">
              We implement appropriate technical and organizational measures to protect your personal information against unauthorized access, alteration, disclosure, or destruction. However, no method of transmission over the internet or electronic storage is 100% secure, and we cannot guarantee absolute security.
            </p>
          </section>

          <section className="mb-12">
            <h2 className="font-cormorant text-3xl font-semibold mb-4">Data Retention</h2>
            <p className="text-taupe leading-relaxed">
              We retain your personal information only for as long as necessary to fulfill the purposes outlined in this Privacy Policy, unless a longer retention period is required or permitted by law. Health and wellness information is retained in accordance with professional standards and legal requirements.
            </p>
          </section>

          <section className="mb-12">
            <h2 className="font-cormorant text-3xl font-semibold mb-4">Third-Party Links</h2>
            <p className="text-taupe leading-relaxed">
              Our website may contain links to third-party websites (e.g., WhatsApp, social media). We are not responsible for the privacy practices of these external sites. We encourage you to review their privacy policies before providing any personal information.
            </p>
          </section>

          <section className="mb-12">
            <h2 className="font-cormorant text-3xl font-semibold mb-4">Children's Privacy</h2>
            <p className="text-taupe leading-relaxed">
              Our services are not directed to individuals under the age of 18. We do not knowingly collect personal information from children. If you believe we have collected information from a child, please contact us immediately.
            </p>
          </section>

          <section className="mb-12">
            <h2 className="font-cormorant text-3xl font-semibold mb-4">Changes to This Privacy Policy</h2>
            <p className="text-taupe leading-relaxed">
              We may update this Privacy Policy from time to time. We will notify you of any changes by posting the new Privacy Policy on this page and updating the "Last updated" date. We encourage you to review this Privacy Policy periodically.
            </p>
          </section>

          <section className="mb-12">
            <h2 className="font-cormorant text-3xl font-semibold mb-4">Contact Us</h2>
            <p className="text-taupe leading-relaxed mb-4">
              If you have any questions about this Privacy Policy or wish to exercise your data protection rights, please contact us:
            </p>
            <div className="bg-sand/30 p-6 rounded-lg">
              <p className="text-taupe mb-2"><strong>Holis Massage</strong></p>
              <p className="text-taupe mb-2">Email: <a href="mailto:info@holismassage.com" className="text-bronze hover:underline">info@holismassage.com</a></p>
              <p className="text-taupe mb-2">WhatsApp: <a href="https://wa.me/41796017337" className="text-bronze hover:underline" target="_blank" rel="noopener noreferrer">+41 79 601 73 37</a></p>
              <p className="text-taupe">Location: Zurich & St. Moritz, Switzerland</p>
            </div>
          </section>

          <section className="mb-12 bg-bronze/10 p-6 rounded-lg">
            <h2 className="font-cormorant text-2xl font-semibold mb-3">Consent</h2>
            <p className="text-taupe leading-relaxed">
              By using our website and services, you hereby consent to our Privacy Policy and agree to its terms.
            </p>
          </section>
        </motion.div>
      </main>

      {/* Footer */}
      <footer className="py-8 border-t border-taupe/10">
        <div className="container mx-auto px-4 text-center">
          <p className="text-sm text-taupe">
            © {new Date().getFullYear()} Holis Massage. All rights reserved.
          </p>
        </div>
      </footer>
    </div>
  );
};
